import { useTranslation } from 'react-i18next'
import i18n from '@/app/i18n'
import { formatDuration } from '@/shared/lib/format'
import type { NasHealth } from './nasApi'

/**
 * Accounting oynasini o'qiladigan ko'rinishda: "7 kun".
 *
 * ⚠️ `formatDuration` bu yerda YOLG'IZ YETMAYDI — u 604800 soniyani
 * "7 kun 0 soat 0 daq" qilib beradi. Bu texnik jihatdan to'g'ri, lekin
 * matn ichida ("oxirgi 7 kun 0 soat 0 daq ichida 4 ta") o'qilmaydigan
 * bo'lib qoladi va operator raqamga emas, shovqinga qaraydi.
 *
 * Butun kunga bo'linmasa `formatDuration` ga qaytiladi: sozlama
 * ixtiyoriy qiymat qabul qiladi va uni "taxminan 7 kun" deb yaxlitlash
 * yolg'on aniqlik bo'lardi.
 */
export function formatWindow(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds <= 0) return formatDuration(seconds)
  const days = seconds / 86_400
  return Number.isInteger(days) ? `${days} ${i18n.t('units.duration.d')}` : formatDuration(seconds)
}

/**
 * Holatning SABABI — bir gapda, aniq raqamlar bilan.
 *
 * <h2>⚠️ "Aloqa yo'q" yolg'iz YETMAYDI</h2>
 * Operator "qachondan beri" va "nimaga solishtirildi" ni bilmasa qaror
 * qabul qila olmaydi: 40 soniyalik jimlik bilan 14 daqiqalik jimlik
 * butunlay boshqa narsa, lekin bir xil yorliq ostida ko'rinardi.
 * Shuning uchun har matnda o'tgan vaqt bor, `SILENT` da esa chegara ham.
 *
 * <h2>⚠️ Vaqt SERVERDAN, `Date.now()` dan EMAS</h2>
 * `silentSeconds` backendda hisoblangan. Uni brauzerda qayta hisoblash
 * operator mashinasining soati noto'g'ri bo'lganda "14 daqiqa" o'rniga
 * butunlay boshqa raqam berardi — va aynan shu raqamga qarab "routerga
 * borish kerakmi" degan qaror qabul qilinadi.
 *
 * <h2>Nega alohida fayl</h2>
 * `NasHealthTag.tsx` faqat komponent eksport qilishi kerak
 * (`react-refresh/only-export-components`): aralashtirilganda Vite'ning
 * tez qayta yuklashi butun modulni almashtirib, komponent holatini
 * yo'qotadi.
 */
export function useNasHealthReason(): (health: NasHealth, windowSeconds: number | null) => string {
  const { t } = useTranslation()

  return (health, windowSeconds) => {
    const ago = formatDuration(health.silentSeconds)
    switch (health.state) {
      case 'ONLINE':
        return t('nas.health.reason.ONLINE', { ago })
      case 'SILENT':
        return t('nas.health.reason.SILENT', {
          ago,
          sessions: health.activeSessions,
          threshold: formatDuration(health.thresholdSeconds),
        })
      case 'IDLE':
        return t('nas.health.reason.IDLE', { ago })
      case 'NO_DATA':
        return t('nas.health.reason.NO_DATA', { window: formatWindow(windowSeconds) })
      case 'UNMATCHED':
        return t('nas.health.reason.UNMATCHED')
    }
  }
}
